/** Recipes and consumption. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enRecipes = {
  // ---- Recipe editor (back office menu) -------------------------------------------------
  'recipes.action': 'Recipe',
  'recipes.openNamed': 'Recipe of {name}',
  'recipes.openGroupNamed': 'Option recipes of {name}',
  'recipes.countOf': '{count}/{total}',
  'recipes.titleFor': 'Recipe — {name}',
  'recipes.itemDesc':
    'Ingredients for one portion. Stock is reduced automatically once an order is sent to the kitchen.',
  'recipes.optionsTitle': 'Option recipes — {name}',
  'recipes.optionsDesc':
    'Extra ingredients an option adds to a portion (e.g. an extra egg), on top of the dish recipe.',
  'recipes.optionDesc':
    'Extra ingredients for one portion with this option, on top of the dish recipe.',
  'recipes.backToOptions': 'Back to options',
  'recipes.noOptions': 'This group has no options yet — add options to the group first.',
  'recipes.none': 'No recipe',
  'recipes.readOnly': 'Your role can view recipes but not change them.',
  'recipes.emptyReadOnly':
    'No recipe yet — once a manager adds one, sales of this item consume its ingredients.',
  'recipes.inactiveHint': 'Inactive: sales do not consume these ingredients.',
  'recipes.col.recipe': 'Cost / food cost',
  'recipes.col.ingredient': 'Ingredient',
  'recipes.col.qty': 'Qty per portion',
  'recipes.col.unit': 'Unit',
  'recipes.col.cost': 'Cost',
  'recipes.componentsTitle': 'Ingredients',
  'recipes.componentsDesc':
    'Quantity per portion in any unit of the ingredient; up to {max} ingredients.',
  'recipes.componentLabel': 'Ingredient {n}',
  'recipes.pickItem': 'Choose an ingredient',
  'recipes.addComponent': 'Add ingredient',
  'recipes.removeComponent': 'Remove ingredient {n}',
  'recipes.noItems': 'No inventory items yet — add items under Stock › Items first.',
  'recipes.perPortion': '= {qty} per portion',
  'recipes.lineCost': 'Cost',
  'recipes.noAverageCost': 'no average cost yet',
  'recipes.costPerPortion': 'Cost per portion',
  'recipes.costBasis': 'At the current weighted-average cost of this branch.',
  'recipes.costEmpty': 'Add ingredients to see the cost.',
  'recipes.costMissing':
    '{count} ingredient(s) have no average cost yet — the cost appears after stock is received.',
  'recipes.costIncomplete': 'Complete every row to see the cost.',
  'recipes.savedCost': 'Saved recipe:',
  'recipes.foodCost': 'Food cost',
  'recipes.foodCostOf': 'of the selling price',
  'recipes.foodCostNoPrice': 'No selling price to compare with.',
  'recipes.foodCostHigh': 'High',
  'recipes.foodCostShort': 'Food cost {percent}',
  'recipes.notes': 'Notes',
  'recipes.notesHint': 'Preparation notes for the kitchen, up to {max} characters.',
  'recipes.activeLabel': 'Active',
  'recipes.activeHint': 'Inactive recipes are kept but sales no longer consume their ingredients.',
  'recipes.appliesNote':
    'Changes apply to sales not processed yet; ingredients already taken from stock stay as they were.',
  'recipes.saved': 'Recipe of {name} saved.',
  'recipes.err.item': 'Choose an ingredient.',
  'recipes.err.duplicate': 'This ingredient is listed twice — combine the quantities.',
  'recipes.err.qty': 'Enter a quantity above 0 (up to 3 decimals).',
  'recipes.err.unit': 'Choose a unit.',
  'recipes.err.qtyPrecision': 'This quantity cannot be converted exactly — use a smaller unit.',
  'recipes.err.noComponents': 'Add at least one ingredient.',
  'recipes.err.tooMany': 'A recipe can have at most 50 ingredients.',
  'recipes.err.notes': 'Notes can be at most 300 characters.',
  'recipes.err.exists':
    'This item already has a recipe (perhaps just added by someone else). Close and open it again to edit it.',

  // ---- Branch setting ----------------------------------------------------------------------
  'recipes.settings.location': 'Consumption location',
  'recipes.settings.auto': 'Automatic (first kitchen location, else the main location)',
  'recipes.settings.saved': 'Saved location',
  'recipes.settings.hint':
    'Sales take recipe ingredients from this stock location. Items that are short there are recorded as a shortfall — the sale is never blocked.',
  'recipes.settings.noAccess': 'Choosing a location needs permission to view stock.',
  'recipes.settings.loadFailed': 'Could not load the stock locations — try again later.',

  // ---- Consumption (/stok/pemakaian) -------------------------------------------------------
  'stok.consumption.title': 'Consumption',
  'stok.consumption.subtitle':
    'Ingredients used by sales at {branch}: recipe × portions, against what was taken from stock.',
  'stok.consumption.readOnly':
    'Your role can view consumption but not process it — sales are processed automatically every minute.',
  'stok.consumption.rangeTitle': 'Period',
  'stok.consumption.rangeDesc':
    'By the date the sales were processed (not the order date), at most {max} days.',
  'stok.consumption.rangeTooLong': 'Choose a period of at most 93 days.',
  'stok.consumption.period': '{from} – {to}',
  'stok.consumption.backlogUnknown': 'Sales waiting to be processed: loading…',
  'stok.consumption.backlogNone': 'All accepted sales are processed.',
  'stok.consumption.backlogCount': '{count} accepted sale item(s) waiting to be processed.',
  'stok.consumption.run.title': 'Process sales',
  'stok.consumption.run.desc':
    'Sales sent to the kitchen are processed automatically every minute. Process now to see the stock effect immediately.',
  'stok.consumption.run.action': 'Process now',
  'stok.consumption.run.busy': 'Processing…',
  'stok.consumption.run.confirmTitle': 'Process sales now?',
  'stok.consumption.run.confirmBody':
    'Recipe ingredients of all accepted sales not processed yet are taken from the consumption location.',
  'stok.consumption.run.confirmWarning':
    'What is not available is recorded as a shortfall; stock never goes negative.',
  'stok.consumption.run.done': '{count} sale item(s) processed.',
  'stok.consumption.run.resultTitle': 'Last run',
  'stok.consumption.run.processed': 'Processed',
  'stok.consumption.run.posted': 'Fully taken',
  'stok.consumption.run.shortfall': 'Short of stock',
  'stok.consumption.run.noRecipe': 'No recipe',
  'stok.consumption.run.failed': 'Failed',
  'stok.consumption.run.reversed': 'Cancelled, returned',
  'stok.consumption.run.keptAsWaste': 'Cancelled, kept as waste',
  'stok.consumption.run.remaining':
    '{count} item(s) still waiting — they are processed in the next run.',
  'stok.consumption.run.remainingNone': 'Nothing left to process.',
  'stok.consumption.stat.value': 'Ingredient value used',
  'stok.consumption.stat.valueDesc': 'Average cost taken from stock, net of returns ({currency})',
  'stok.consumption.stat.items': 'Sale items processed',
  'stok.consumption.stat.itemsDesc': 'Accepted order items in the period',
  'stok.consumption.stat.noRecipe': '{count} without a recipe',
  'stok.consumption.stat.shortfall': 'Ingredients short',
  'stok.consumption.stat.shortfallDesc': 'Required more than was in usable stock',
  'stok.consumption.stat.shortfallItems': '{count} sale item(s) affected',
  'stok.consumption.stat.backlog': 'Waiting',
  'stok.consumption.stat.backlogDesc': 'Accepted sale items not processed yet',
  'stok.consumption.ingredients': 'Per ingredient',
  'stok.consumption.ingredientsDesc':
    'Required by recipes vs taken from stock. A shortfall means the stock records are behind reality — reconcile with a stock count.',
  'stok.consumption.empty':
    'No consumption in this period — it appears once sales of items with a recipe are processed.',
  'stok.consumption.col.required': 'Required',
  'stok.consumption.col.posted': 'Taken',
  'stok.consumption.col.shortfall': 'Shortfall',
  'stok.consumption.short': 'Short',
  'stok.consumption.shortfalls': 'Sales not fully consumed',
  'stok.consumption.shortfallsDesc':
    'Sale items whose ingredients could not all be taken, and why.',
  'stok.consumption.shortfallsEmpty': 'Every processed sale item was fully taken from stock.',
  'stok.consumption.col.menuItem': 'Sale item',
  'stok.consumption.col.consumedAt': 'Processed at',
  'stok.consumption.col.why': 'Why',
  'stok.consumption.status.shortfall': 'Short',
  'stok.consumption.status.failed': 'Failed',
  'stok.consumption.why.shortfall':
    'Not enough usable stock at the consumption location (expired and held lots are never used).',
  'stok.consumption.why.failed': 'Could not be processed.',
  'stok.consumption.def.scope': 'Scope',
  'stok.consumption.def.required': 'Required',
  'stok.consumption.def.posted': 'Taken',
  'stok.consumption.def.shortfall': 'Shortfall',
  'stok.consumption.def.value': 'Value',
} as const;
